import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import Moment from 'react-moment';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { A, Column, P, Row } from '../../../../../assets/elements';
import InfoIcon from '../../../../../assets/icons/Info.svg';
import PrinterIcon from '../../../../../assets/icons/Printer.svg';
import device from '../../../../utils/device';
import { dateFormat } from '../../../../utils/variables';
import { setParcelCollectionDate } from '../../../../store/actions';
import { SelectInput } from '../../../Inputs';

const SingleQuote = ({
  id,
  name,
  logo,
  status = '',
  deliveryTime,
  collectionDate,
  price,
  priceIncVat,
  type,
  printer,
  moreInfo,
  setMoreInfo,
  openMoreInfoModal,
  dropoffPoints = [],
  onSelect,
  selected,
  active,
  pricesBeforeDiscount,
  sameday,
  collectionDays,
  holidays,
}: any) => {
  const dispatch = useDispatch();
  const [nearestDropoff, setNearestDropoff] = useState('');

  const [collectionDates, setCollectionDates] = useState<any>([]);

  const handleSubmit = () => {
    onSelect(id);
  };

  const deliveryTimeFormat = (days: number) =>
    `${days} ${days === 0 || days > 2 ? 'days' : 'day'}`;

  const handleMoreInfoClick = (e: any) => {
    e.stopPropagation();
    setMoreInfo(moreInfo);
    ReactTooltip.hide();
    openMoreInfoModal();
  };

  useEffect(() => {
    if (dropoffPoints.length > 0) {
      const { distance = {} } = dropoffPoints[0];
      setNearestDropoff(`${distance?.amount}${distance?.unit}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (type === 'collection') {
      setCollectionDates(generateCollectionDates(holidays));
    }
  }, [holidays]);

  const setCollectionDate = (date: any) => {
    dispatch(setParcelCollectionDate({ id, date }));
  };

  const generateCollectionDates = (holidayList: any) => {
    let days = 5;
    const dates: any[] = [];
    const tempDate = moment(collectionDate).subtract(1, 'days');

    while (days > 0) {
      const newDate = tempDate.add(1, 'days');

      if (
        holidayList.filter((holiday: any) => moment(holiday).isSame(moment(newDate), 'days')).length === 0 &&
        collectionDays.includes(newDate.isoWeekday())
      ) {
        dates.push({
          label: newDate.format(dateFormat),
          value: newDate.format('yyyy-MM-DD'),
        });
        days -= 1;
      }
    }

    return dates;
  };

  return (
    <RowStyled selected={selected} active={active} onClick={handleSubmit}>
      <ColumnStyled sizeL={2} sizeM={2} sizeXS={4}>
        <Brand>
          <BrandImg src={logo} alt={`${name} logo`} />
        </Brand>
      </ColumnStyled>
      <ColumnStyled sizeL={3} sizeM={10} sizeXS={4}>
        <InfoContainer>
          <TitleContainer>
            <P>{name}</P>
            <IconsContainer>
              <Icon
                onClick={handleMoreInfoClick}
                data-tip
                data-for="info-icon"
                src={InfoIcon}
              />
              <ReactTooltip id="info-icon" effect="solid">
                More information
              </ReactTooltip>

              {printer && (
                <>
                  <Icon data-tip data-for="printer-icon" src={PrinterIcon} />
                  <ReactTooltip id="printer-icon" effect="solid">
                    Printer required
                  </ReactTooltip>
                </>
              )}
            </IconsContainer>
          </TitleContainer>
          <Status>{`Covid 19 - ${status}`}</Status>
        </InfoContainer>
      </ColumnStyled>
      <ColumnStyledSmall sizeL={2.3} sizeM={4} sizeXS={4}>
        <ParcelTypeContainer type={type}>
          <ParcelTypeHead type={type}>
            {type === 'collection' ? 'Collection' : 'Drop off'}
          </ParcelTypeHead>
          <ParcelTypeData>
            {type === 'collection' && (
              sameday ? (
                <Moment format={dateFormat}>{collectionDate}</Moment>
              ) : (
                <SelectContainer>
                  <SelectInput
                    error={false}
                    resetError={() => {}}
                    value={collectionDate}
                    onChange={(value: any) => setCollectionDate(value)}
                    data={collectionDates}
                    height="40px"
                    width="100%"
                    minWidth="unset"
                    fontSize="14px"
                  />
                </SelectContainer>
              )
            )}
            {type === 'dropoff' && (
              <DropOffMessage>
                Nearest drop off {nearestDropoff}
              </DropOffMessage>
            )}
          </ParcelTypeData>
        </ParcelTypeContainer>
      </ColumnStyledSmall>
      <Column sizeL={2.3} sizeM={4} sizeXS={2}>
        <DeliveryTimeContainer>
          <DeliveryTimeHead>Delivery time</DeliveryTimeHead>
          <DeliveryTimeData>
            {deliveryTimeFormat(deliveryTime)}
          </DeliveryTimeData>
        </DeliveryTimeContainer>
      </Column>
      <ColumnPrice sizeL={2.3} sizeM={4} sizeXS={2}>
        <PriceContainer>
          <PriceHead>Price</PriceHead>
          <ActualPrice>
            <Price>£{ price }</Price>
            {
              pricesBeforeDiscount &&
              <PriceOriginal>£{ Number(pricesBeforeDiscount.price).toFixed(2) }</PriceOriginal>
            }
            <PriceVAT>incl. VAT {`£${priceIncVat}`}</PriceVAT>
          </ActualPrice>
        </PriceContainer>
      </ColumnPrice>
    </RowStyled>
  );
};

const RowStyled = styled(Row).attrs(({ selected = false, active }: any) => ({
  selected,
  active,
}))`
  background: ${({ theme }: any) => theme.colors.White};
  width: 100%;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: ${({ theme }: any) => theme.box.BorderRadius};
  border: 2px solid;
  border-color: ${({ theme, selected }: any) => selected ? theme.colors.Secondary : theme.colors.LightGrey};
  cursor: pointer;
  display: ${({ active }: any) => active ? 'flex' : 'none'};
`;

const ColumnStyled = styled(Column)`
  margin-bottom: 24px;

  @media ${device.laptopL} {
    margin-bottom: 0;
  }
`;

const ColumnStyledSmall = styled(Column)`
  margin-bottom: 24px;

  @media ${device.laptop} {
    margin-bottom: 0;
  }
`;

const Brand = styled.div``;
const BrandImg = styled.img`
  max-height: 88px;
  width: auto;
  max-width: 100px;
`;
const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
`;
const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Status = styled.span`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 100%;
  display: flex;
  align-items: center;
  color: #777e7e;
  flex: none;
  order: 1;
  align-self: flex-start;
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 76px;
  flex: 0 0 76px;
`;

const Icon = styled.img``;

const ParcelTypeContainer = styled.div.attrs(({ type }: any) => ({
  type,
}))`
  display: flex;
  flex-direction: column;
  padding: 8px;
  background: ${({ type, theme }: any) =>
    type === 'collection' ? theme.colors.CollectionBackground : theme.colors.DropoffBackground};
  align-items: center;
  border-radius: 4px;
  height: 100%;
`;

const ParcelTypeHead = styled.div.attrs(({ type }: any) => ({
  type,
}))`
  background: ${({ type, theme }: any) =>
    type === 'collection' ? theme.colors.CollectionHead : theme.colors.DropoffHead};
  border-radius: 2px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 125%;
  display: flex;
  align-items: center;
  text-align: center;
  width: 100%;
  color: ${({ theme }: any) => theme.colors.White};
  padding: 7px 4px;
  justify-content: center;
`;

const ParcelTypeData = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  text-align: center;
  justify-content: center;
  font-size: 19px;
  line-height: 150%;
  font-weight: 500;
  margin-top: 13px;
`;

const DeliveryTimeContainer = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  background: ${({ theme }: any) => theme.colors.WhiteGrey};
  color: ${({ theme }: any) => theme.colors.Black};
  border-radius: 4px;
  height: 100%;
`;

const DeliveryTimeHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 125%;
  padding: 7px 4px;
`;

const DeliveryTimeData = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  font-size: 19px;
  line-height: 150%;
  font-weight: 500;
  margin-top: 13px;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  padding: 8px;
  height: 100%;
  background: ${({ theme }: any) => theme.colors.Pastel2};
  border-radius: 4px;
`;

const PriceHead = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 19px;
  line-height: 150%;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }: any) => theme.colors.Black};

  @media ${device.laptopL} {
    font-size: 14px;
    line-height: 125%;
  }
`;

const Price = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }: any) => theme.colors.Black};
  justify-content: center;
`;

const PriceVAT = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ theme }: any) => theme.colors.MidGrey};
  justify-content: center;
`;

const DropOffMessage = styled(A)`
  text-decoration: underline;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 135%;
  color: ${({ theme }: any) => theme.colors.DropoffHead};
`;

const ColumnPrice = styled(Column)``;

const ActualPrice = styled.div`
  width: 100%;

  @media ${device.laptopL} {
    width: unset;
  }
`;

const PriceOriginal = styled.p`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  text-decoration: line-through;
  margin-bottom: 4px;
`;

const SelectContainer = styled.div`
  display: flex;
  padding: 5px;
  width: 100%;
`;

export default SingleQuote;
